import { useLanguage, LANGUAGES } from '@/i18n/LanguageContext';
import { Globe } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export default function LanguageSelector({ compact = false, showLabel = false, className = '' }) {
  const { language, setLanguage, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (showLabel) {
    return (
      <div className={className} data-testid="language-selector-settings">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`flex items-center gap-2.5 px-4 py-3 rounded-lg border-2 text-sm transition-all ${
                language === lang.code 
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-medium shadow-sm' 
                  : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`lang-settings-${lang.code}`}
            >
              <span className="text-lg">{lang.flag}</span>
              <div className="text-left">
                <div className="font-medium">{lang.name}</div>
                {lang.dir === 'rtl' && <span className="text-[10px] text-gray-400">RTL</span>}
              </div>
              {language === lang.code && (
                <svg className="w-4 h-4 text-indigo-500 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={`relative ${className}`} data-testid="language-selector">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:border-gray-300 bg-white text-sm transition-colors"
        data-testid="language-selector-btn"
      >
        <Globe className="w-4 h-4 text-gray-500" />
        {!compact && <span>{current.name}</span>}
        {compact && <span className="text-xs">{current.code.toUpperCase()}</span>}
      </button>

      {open && (
        <div className="absolute top-full mt-1 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[180px] max-h-[320px] overflow-y-auto" data-testid="language-dropdown">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => { setLanguage(lang.code); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                language === lang.code ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="text-base">{lang.flag}</span>
              <span>{lang.name}</span>
              {lang.dir === 'rtl' && <span className="text-[10px] text-gray-400 ml-auto">RTL</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
